import { useMemo, type FC } from 'react'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId, selectAuthenticatedUser } from '../../../store/auth/authSlice'
import { type ExternalUserServiceIntegration } from '../../../models/User'
import { useGetUserIntegrationInfoQuery } from '../../../store/user/user'
import { Tooltip } from '../../Tooltip/Tooltip'
import { IntegrationServiceId } from '../../../common/integrations'
import { DelegatedEmailIntegrationState } from './DelegatedEmailIntegrationState'

export type IntegrationStateProps = {
  serviceId: IntegrationServiceId
  currentIntegration?: ExternalUserServiceIntegration
  className?: string
}

const integrationStateOverrides: Partial<Record<IntegrationServiceId, FC<IntegrationStateProps>>> = {
  [IntegrationServiceId.DELEGATED_EMAIL]: DelegatedEmailIntegrationState,
}

const DefaultIntegrationState: FC<IntegrationStateProps> = ({ currentIntegration, className }) => {
  const integrationState: string | undefined = useMemo(() => {
    return currentIntegration?.state
  }, [currentIntegration])
  const integrationType: string | undefined = useMemo(() => {
    return currentIntegration?.type
  }, [currentIntegration])
  const integrationId: string | undefined = useMemo(() => {
    return currentIntegration?.id
  }, [currentIntegration])
  const accountId = useAppSelector(selectAccountId)
  const currentUser = useAppSelector(selectAuthenticatedUser)

  const { data: userIntegrationInfoData, isLoading: userIntegrationInfoIsLoading } = useGetUserIntegrationInfoQuery(
    {
      accountId: accountId,
      userId: currentUser?.id ?? '',
    },
    { skip: !currentUser?.id },
  )
  if ((!integrationState && integrationType !== 'oauth2_app') || !integrationId || !currentUser || !currentIntegration) {
    return null
  }
  if (userIntegrationInfoIsLoading) {
    return null
  }

  if (integrationState === 'live' || integrationType === 'oauth2_app') {
    const connected = (
      <div className={`zocks-text-success ${className}`}>
        <i className='bi bi-check fs-5' />
        <span>Connected</span>
      </div>
    )
    return currentIntegration.email ? (
      <div className='d-flex flex-column'>
        {connected}
        <span className='zocks-text-gray-600 small'>{currentIntegration.email}</span>
      </div>
    ) : (
      connected
    )
  }
  const integrationProblems = userIntegrationInfoData?.data.problems.filter((issue) => issue.integrationId === integrationId) ?? []
  return (
    <div className={`${className} zocks-text-danger`}>
      <i className='bi bi-exclamation fs-5' />
      {integrationProblems.length > 0 ? (
        <Tooltip id='integration-info-tooltip' className='integration-info-tooltip integration-state'>
          {integrationProblems.map((problem) => {
            return (
              <div key={problem.issue}>
                <b>{problem.issue}</b>
                <br />
              </div>
            )
          })}
        </Tooltip>
      ) : (
        <span>Your integration is invalid. Please reconnect.</span>
      )}
    </div>
  )
}

export const IntegrationState: FC<IntegrationStateProps> = ({ serviceId, currentIntegration, className }) => {
  const Component = integrationStateOverrides[serviceId] ?? DefaultIntegrationState
  return <Component serviceId={serviceId} currentIntegration={currentIntegration} className={className} />
}
